import type { CheckoutLineView, CheckoutSessionState } from "@cx/contracts";
import { formatQty, type CheckoutMode, type CheckoutProgress } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";

export const CHECKOUT_MODE_LABEL: Record<CheckoutMode, string> = {
  by_order: "Por pedido",
  single_product: "Por produto único",
};
export const CHECKOUT_STATE_LABEL: Record<CheckoutSessionState, string> = {
  open: "Em conferência",
  finished: "Concluída",
  canceled: "Cancelada",
};

/** Quantidade em q4 para a tela ("2", "1,5"). */
export const qty = (q4: number) => formatQty(q4, 0);

/** Percentual inteiro conferido (arredonda para baixo: 99,9% não aparece como 100%). */
export function progressPct(p: Pick<CheckoutProgress, "requiredQ4" | "checkedQ4">): number {
  if (p.requiredQ4 <= 0) return 0;
  return Number((BigInt(p.checkedQ4) * 100n) / BigInt(p.requiredQ4));
}

export type LineState = "done" | "partial" | "pending";
export const lineState = (l: Pick<CheckoutLineView, "requiredQ4" | "checkedQ4">): LineState =>
  l.checkedQ4 >= l.requiredQ4 ? "done" : l.checkedQ4 > 0 ? "partial" : "pending";
export const LINE_STATE_LABEL: Record<LineState, string> = {
  done: "Conferido",
  partial: "Parcial",
  pending: "Pendente",
};

/** Mensagens do checkout: as da API já falam do caso (o "limit_reached" genérico das telas de cadastro não serve). */
const OWN = new Set(["limit_reached", "not_found", "order_not_ready", "order_changed", "session_closed"]);
export function checkoutError(e: unknown): string {
  if (e instanceof ApiFailure && OWN.has(e.code)) return e.message;
  if (e instanceof ApiFailure && e.code === "too_many_connections")
    return "Muitas telas de checkout abertas: feche alguma para receber as atualizações ao vivo.";
  return errorMessage(e);
}

/**
 * Endereço do WebSocket do checkout no mesmo host da SPA (o cookie de sessão vai junto). Com `sessionId` (tela da
 * conferência) recebe também as bipagens daquela sessão; sem, só as mudanças de estado (fila).
 */
export function checkoutSocketUrl(
  loc: Pick<Location, "protocol" | "host">,
  companyId: string,
  sessionId?: string,
): string {
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  const q = new URLSearchParams({ companyId, ...(sessionId ? { sessionId } : {}) });
  return `${scheme}//${loc.host}/api/v1/checkout/ws?${q}`;
}

/**
 * Chama `fn` na hora e, se vierem mais chamadas dentro de `ms`, uma vez só no fim da janela (eventos em rajada viram
 * no máximo uma recarga a cada `ms`). `cancel` descarta a pendente (desmontar a tela).
 */
export function throttle(fn: () => void, ms: number): (() => void) & { cancel: () => void } {
  let last = -Infinity;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    timer = undefined;
    last = Date.now();
    fn();
  };
  const t = () => {
    if (timer) return;
    const wait = last + ms - Date.now();
    if (wait <= 0) run();
    else timer = setTimeout(run, wait);
  };
  t.cancel = () => {
    clearTimeout(timer);
    timer = undefined;
  };
  return t;
}

/** Espera antes de reconectar: 1 s, 2 s, 4 s… até 30 s. */
export const reconnectDelay = (attempt: number) => Math.min(30_000, 1_000 * 2 ** Math.max(0, attempt));

/** Ids da seleção para o relatório de separação (vírgula, na URL). */
export const pickingIds = (ids: Iterable<string>) => [...ids].join(",");
